import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaSyncAlt,
  FaSearch,
  FaExternalLinkAlt,
  FaFilePdf,
  FaEnvelope,
  FaLink,
  FaInfoCircle,
  FaExclamationTriangle,
  FaClock,
  FaUsers,
  FaBookOpen,
  FaScroll,
  FaCalendarAlt,
  FaChevronRight,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechTabs,
  TechInput,
  TechButton,
  TechChip,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  Empty,
  ErrorBox,
  Loading,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { getHcdSnapshot, searchHcd } from '../../services/monitoringService';
import { CitePopup } from '../UI/Citations';
import HcdChat from './HcdChat';
import CronCountdown from '../common/CronCountdown';

/* ---------- Styled primitives ---------- */

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  padding: 0.85rem 0.95rem;
  background: rgba(36, 52, 77, 0.55);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  margin: 1rem 0 1.1rem;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 240px;

  > svg {
    position: absolute;
    left: 0.7rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-disabled);
    font-size: 0.82rem;
  }
  input { padding-left: 2rem; }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--text-disabled);

  svg { color: ${accent}; }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.85rem;
`;

const AuthoritiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 1.25rem;
  width: 100%;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Card = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 0.85rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;

  .title {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.92rem;
  }
  .sub {
    color: var(--text-disabled);
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }
  .row {
    display: flex;
    gap: 0.45rem;
    align-items: center;
    color: var(--text-secondary);
    font-size: 0.83rem;
  }
`;

const AvatarImg = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid ${borderSoft};
  background: rgba(0, 180, 216, 0.08);
  flex-shrink: 0;
`;

const AvatarFallback = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 1px solid rgba(0, 180, 216, 0.3);
  background: rgba(0, 180, 216, 0.08);
  color: ${accent};
  font-family: ${mono};
  font-size: 1.05rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.05em;
`;

function authorityInitials(name) {
  if (!name) return '?';
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
}

const AuthorityAvatar = ({ foto, nombre }) => {
  const [broken, setBroken] = React.useState(false);
  if (foto && !broken) {
    return (
      <AvatarImg
        src={foto}
        alt=""
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }
  return <AvatarFallback>{authorityInitials(nombre)}</AvatarFallback>;
};

const AuthorityCard = styled(Link)`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  padding: 1.15rem 1.25rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.1rem;
  text-decoration: none;
  color: inherit;
  position: relative;
  min-width: 0;
  height: 100%;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.45);
    background: rgba(0, 180, 216, 0.05);
    transform: translateY(-1px);
  }

  .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .title {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.98rem;
    line-height: 1.25;
  }
  .sub {
    color: var(--text-disabled);
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }
  .row {
    display: flex;
    gap: 0.45rem;
    align-items: center;
    color: var(--text-secondary);
    font-size: 0.83rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chevron {
    color: var(--text-disabled);
    flex-shrink: 0;
    transition: color 0.18s ease, transform 0.18s ease;
  }
  &:hover .chevron {
    color: ${accent};
    transform: translateX(3px);
  }
`;

const ListBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Row = styled.a`
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  padding: 0.55rem 0.7rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  text-decoration: none;
  color: var(--text-secondary);
  font-size: 0.84rem;
  transition: all 0.15s ease;

  &:hover {
    color: var(--text-primary);
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.04);
  }

  .icon { flex-shrink: 0; color: ${accent}; padding-top: 2px; }
  .body { flex: 1; min-width: 0; }
  .meta {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-top: 2px;
  }
  .num { color: ${accent}; font-weight: 600; }
`;

const YearStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.85rem;
`;

const YearChip = styled.button`
  background: ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.15)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.4)' : borderSoft)};
  padding: 0.35rem 0.7rem;
  border-radius: 5px;
  font-family: ${mono};
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { color: var(--text-primary); border-color: rgba(0, 180, 216, 0.3); }
`;

const SubTitle = styled.h4`
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin: 1rem 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  svg { color: ${accent}; font-size: 0.8rem; }
`;

const TextBlock = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.86rem;
  line-height: 1.55;
  color: var(--text-secondary);
  background: rgba(11, 19, 32, 0.45);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.85rem 0.95rem;
  max-height: 65vh;
  overflow: auto;
`;

const SubTabsRow = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-bottom: 0.8rem;
`;

const SubTab = styled.button`
  background: ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.18)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.4)' : borderSoft)};
  padding: 0.45rem 0.85rem;
  border-radius: 6px;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { color: var(--text-primary); }
`;

/* ---------- helpers ---------- */

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; }
}

const DIACRITICS_RE = new RegExp('[\\u0300-\\u036f]', 'g');
function slugifyName(input) {
  return String(input || '')
    .normalize('NFD')
    .replace(DIACRITICS_RE, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

// El HCD entrega miembros de comisión como "APELLIDO, NOMBRE".
// La mesa los entrega como "NOMBRE APELLIDO". Normalizamos al segundo
// formato para que el slug del miembro de comisión matchee con el de
// la mesa cuando es la misma persona.
function flipLastFirstFront(name) {
  const s = String(name || '').trim();
  if (s.includes(',')) {
    const [last, ...rest] = s.split(',').map((p) => p.trim()).filter(Boolean);
    return [rest.join(' ').trim(), last].filter(Boolean).join(' ');
  }
  return s;
}

/* ============== Búsqueda RAG (FTS + semántica) ============== */

const SOURCE_TYPE_LABELS = {
  profile: 'Perfil',
  comision: 'Comisión',
  digesto: 'Digesto',
  acta_meta: 'Acta',
  pdf: 'PDF',
  prensa: 'Prensa',
  historia: 'Historia',
  expediente: 'Expediente',
  news_scraped: 'Noticia',
  mesa: 'Mesa',
};

const RAG_TYPE_OPTIONS = [
  { id: '', label: 'Todos' },
  { id: 'pdf', label: 'PDFs' },
  { id: 'digesto', label: 'Digesto' },
  { id: 'expediente', label: 'Expedientes' },
  { id: 'profile', label: 'Perfiles' },
  { id: 'news_scraped', label: 'Noticias' },
  { id: 'prensa', label: 'Prensa HCD' },
  { id: 'acta_meta', label: 'Actas' },
  { id: 'historia', label: 'Historia' },
  { id: 'comision', label: 'Comisiones' },
];

const SUGGESTED_QUERIES = [
  'ordenanzas sobre humedales y medio ambiente',
  'expediente sobre el puerto',
  'cesión de inmueble a vecinos',
  'rendición de cuentas',
  'autorización para evento público',
  'multa al concejo',
  'Federico Chouhy posiciones',
  'comisiones internas',
];

const SearchBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

const TypeFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
`;

const TypeChip = styled.button`
  background: ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.16)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.4)' : borderSoft)};
  padding: 0.32rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { color: var(--text-primary); border-color: rgba(0, 180, 216, 0.3); }
`;

const ResultCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  text-align: left;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.55rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.4);
    background: rgba(0, 180, 216, 0.04);
    transform: translateY(-1px);
  }

  .head {
    display: flex;
    gap: 0.55rem;
    align-items: center;
    flex-wrap: wrap;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
  .type {
    background: rgba(0, 180, 216, 0.1);
    color: ${accent};
    border: 1px solid rgba(0, 180, 216, 0.3);
    padding: 2px 7px;
    border-radius: 4px;
  }
  .score {
    color: var(--text-disabled);
  }
  .title {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.92rem;
    line-height: 1.35;
  }
  .preview {
    color: var(--text-secondary);
    font-size: 0.84rem;
    line-height: 1.55;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const SuggestRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
`;

const SuggestPill = styled.button`
  background: transparent;
  border: 1px solid ${borderSoft};
  color: var(--text-secondary);
  font-size: 0.78rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { color: ${accent}; border-color: rgba(0, 180, 216, 0.4); background: rgba(0, 180, 216, 0.05); }
`;

function RagSearch() {
  const [query, setQuery] = useState('');
  const [activeType, setActiveType] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [elapsed, setElapsed] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const [activeRag, setActiveRag] = useState([]);

  const run = async (qOverride, typeOverride) => {
    const q = (qOverride ?? query).trim();
    if (!q || loading) return;
    setLoading(true);
    setErr(null);
    const t0 = Date.now();
    try {
      const types = (typeOverride ?? activeType) ? [(typeOverride ?? activeType)] : null;
      const data = await searchHcd({ query: q, topK: 25, types });
      setResults(data?.results || []);
      setElapsed(Date.now() - t0);
    } catch (e) {
      setErr(friendlyError(e, 'Error en la búsqueda'));
    } finally {
      setLoading(false);
    }
  };

  // Convertir resultados a shape de "rag sources" para el CitePopup
  const ragSources = useMemo(
    () => results.map((r, i) => ({
      idx: `RAG-${i + 1}`,
      source_type: r.source_type,
      source_label: r.source_label,
      url: r.url,
      score: r.score,
      extracto: String(r.content || '').slice(0, 1200),
    })),
    [results]
  );

  return (
    <TechSection title="Búsqueda IA sobre todo el HCD (corpus completo indexado)">
      <Meta style={{ marginBottom: '0.85rem' }}>
        <FaInfoCircle />
        <span>Busca por palabra y por significado. Buscás "humedales" y aparecen ordenanzas, expedientes y perfiles relacionados — aunque no usen esa palabra.</span>
      </Meta>

      <SearchBar>
        <SearchWrap>
          <FaSearch />
          <TechInput
            placeholder="¿Qué buscás? Ej: ordenanzas sobre tránsito, expediente puerto, multa al concejo…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            disabled={loading}
          />
        </SearchWrap>
        <TechButton onClick={() => run()} disabled={loading || !query.trim()}>
          {loading ? 'Buscando…' : 'Buscar'}
        </TechButton>
      </SearchBar>

      <TypeFilter>
        {RAG_TYPE_OPTIONS.map((opt) => (
          <TypeChip
            key={opt.id || 'all'}
            $active={activeType === opt.id}
            onClick={() => { setActiveType(opt.id); if (query.trim()) run(query, opt.id); }}
          >
            {opt.label}
          </TypeChip>
        ))}
      </TypeFilter>

      {!query && results.length === 0 && !loading && (
        <SuggestRow>
          {SUGGESTED_QUERIES.map((s) => (
            <SuggestPill key={s} type="button" onClick={() => { setQuery(s); run(s); }}>
              {s}
            </SuggestPill>
          ))}
        </SuggestRow>
      )}

      {err && <ErrorBox>{err}</ErrorBox>}

      {elapsed != null && !loading && (
        <Meta style={{ marginBottom: '0.7rem' }}>
          <FaClock />
          <span>{results.length} resultados en {elapsed}ms</span>
        </Meta>
      )}

      {results.length === 0 && !loading && !err && query && (
        <Empty>Sin resultados para "{query}"</Empty>
      )}

      {results.map((r, i) => {
        const typeLabel = SOURCE_TYPE_LABELS[r.source_type] || r.source_type;
        return (
          <ResultCard
            key={r.id}
            type="button"
            onClick={() => {
              setActiveRag(ragSources);
              setActiveCite({ prefix: 'RAG-', num: String(i + 1) });
            }}
          >
            <div className="head">
              <span className="type">{typeLabel}</span>
              <span className="score">score {Number(r.score || 0).toFixed(3)}</span>
              {r.url && <span style={{ color: accent }}>↗ tiene link</span>}
            </div>
            <div className="title">{r.source_label || 'Sin título'}</div>
            <div className="preview">{String(r.content || '').slice(0, 280)}</div>
          </ResultCard>
        );
      })}

      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={[]}
          fresh={[]}
          rag={activeRag}
          onClose={() => setActiveCite(null)}
        />
      )}
    </TechSection>
  );
}

const TABS = [
  { id: 'overview', label: 'Resumen' },
  { id: 'search', label: 'Búsqueda IA' },
  { id: 'authorities', label: 'Autoridades' },
  { id: 'committees', label: 'Comisiones' },
  { id: 'mesa', label: 'Mesa de Entrada' },
  { id: 'sessions', label: 'Sesiones / Actas' },
  { id: 'digesto', label: 'Digesto' },
  { id: 'press', label: 'Prensa' },
  { id: 'history', label: 'Historia' },
  { id: 'crawl', label: 'Sitio (crawl)' },
];

/* ---------- Sub-views ---------- */

function Overview({ snapshot, stats }) {
  return (
    <TechSection title="Resumen del monitoreo">
      <StatStrip>
        <StatTile><StatLabel>Autoridades</StatLabel><StatValue>{stats?.autoridades ?? 0}</StatValue><StatSub>concejales activos</StatSub></StatTile>
        <StatTile><StatLabel>Comisiones</StatLabel><StatValue>{stats?.comisiones ?? 0}</StatValue><StatSub>internas</StatSub></StatTile>
        <StatTile><StatLabel>Actas</StatLabel><StatValue>{stats?.actas ?? 0}</StatValue><StatSub>{stats?.actas_years ?? 0} años</StatSub></StatTile>
        <StatTile><StatLabel>Notas</StatLabel><StatValue>{stats?.notas ?? 0}</StatValue><StatSub>mesa de entrada</StatSub></StatTile>
        <StatTile><StatLabel>Expedientes</StatLabel><StatValue>{stats?.expedientes_fetched ?? 0}</StatValue><StatSub>de {stats?.expedientes_total_reported ?? 0} reportados</StatSub></StatTile>
        <StatTile><StatLabel>Ordenanzas</StatLabel><StatValue>{stats?.ordenanzas ?? 0}</StatValue><StatSub>digesto</StatSub></StatTile>
        <StatTile><StatLabel>Decretos</StatLabel><StatValue>{stats?.decretos ?? 0}</StatValue><StatSub>digesto</StatSub></StatTile>
        <StatTile><StatLabel>Resoluciones</StatLabel><StatValue>{stats?.resoluciones ?? 0}</StatValue><StatSub>digesto</StatSub></StatTile>
        <StatTile><StatLabel>Comunicaciones</StatLabel><StatValue>{stats?.comunicaciones ?? 0}</StatValue><StatSub>digesto</StatSub></StatTile>
        <StatTile><StatLabel>Noticias</StatLabel><StatValue>{stats?.noticias ?? 0}</StatValue><StatSub>prensa</StatSub></StatTile>
        <StatTile><StatLabel>Historia</StatLabel><StatValue>{Math.round((stats?.historia_chars ?? 0) / 1000)}k</StatValue><StatSub>caracteres</StatSub></StatTile>
        <StatTile><StatLabel>Crawl</StatLabel><StatValue>{snapshot?.crawl?.stats?.pages ?? 0}</StatValue><StatSub>páginas estáticas</StatSub></StatTile>
      </StatStrip>
      <SubTitle><FaInfoCircle /> Tiempo de scrapeo</SubTitle>
      <Meta>
        <FaClock />
        <span>{Math.round((stats?.elapsed_ms ?? 0) / 100) / 10} segundos · {formatDate(snapshot?.started_at)} → {formatDate(snapshot?.finished_at)}</span>
      </Meta>
    </TechSection>
  );
}

function Authorities({ items, query }) {
  const list = Array.isArray(items) ? items : [];
  const filtered = useMemo(() => {
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((a) =>
      (a.nombre || '').toLowerCase().includes(q) ||
      (a.cargo || '').toLowerCase().includes(q) ||
      (a.partido || '').toLowerCase().includes(q)
    );
  }, [list, query]);
  return (
    <TechSection title={`Autoridades (${filtered.length}/${list.length})`}>
      {filtered.length === 0 ? <Empty>Sin coincidencias</Empty> : (
        <>
          <Meta style={{ marginBottom: 10 }}>
            <FaInfoCircle />
            <span>Click en una autoridad para ver su perfil profundo (bio, conexiones, pros/contras, noticias).</span>
          </Meta>
          <AuthoritiesGrid>
            {filtered.map((a) => {
              const slug = slugifyName(a.nombre);
              const personalEmail =
                a.email && !a.email.toLowerCase().startsWith('hcdsannicolas@')
                  ? a.email
                  : null;
              return (
                <AuthorityCard key={a.id || slug} to={`/monitoring/hcd/autoridad/${slug}`}>
                  <AuthorityAvatar foto={a.foto} nombre={a.nombre} />
                  <div className="body">
                    <div className="title">{a.nombre}</div>
                    <div className="sub">{a.cargo}</div>
                    {a.partido && <div className="row">{a.partido}</div>}
                    {a.periodo && <div className="row">Período: {a.periodo}</div>}
                    {personalEmail && (
                      <div className="row">
                        <FaEnvelope />
                        <span>{personalEmail}</span>
                      </div>
                    )}
                  </div>
                  <FaChevronRight className="chevron" />
                </AuthorityCard>
              );
            })}
          </AuthoritiesGrid>
        </>
      )}
    </TechSection>
  );
}

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.65rem;
  margin-top: 0.45rem;
`;

const MemberCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.75rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 7px;
  text-decoration: none;
  color: inherit;
  min-width: 0;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.45);
    background: rgba(0, 180, 216, 0.05);
    transform: translateY(-1px);
  }

  .mini-avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid rgba(0, 180, 216, 0.32);
    background: rgba(0, 180, 216, 0.08);
    color: ${accent};
    font-family: ${mono};
    font-size: 0.78rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    letter-spacing: 0.05em;
  }
  .mini-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .mini-role {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${accent};
  }
  .mini-name {
    color: var(--text-primary);
    font-size: 0.86rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mini-chev {
    color: var(--text-disabled);
    flex-shrink: 0;
    font-size: 0.7rem;
  }
  &:hover .mini-chev { color: ${accent}; }
`;

function memberInitials(name) {
  const flipped = flipLastFirstFront(name);
  if (!flipped) return '?';
  return flipped.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase();
}

function Committees({ items, query }) {
  const list = Array.isArray(items) ? items : [];
  const filtered = useMemo(() => {
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((c) =>
      (c.comision || '').toLowerCase().includes(q) ||
      (c.funcion || '').toLowerCase().includes(q) ||
      (c.miembros || []).some((m) => (m.nombre || '').toLowerCase().includes(q))
    );
  }, [list, query]);
  return (
    <TechSection title={`Comisiones (${filtered.length}/${list.length})`}>
      {filtered.length === 0 ? <Empty>Sin coincidencias</Empty> : filtered.map((c) => (
        <Card key={c.id} style={{ marginBottom: '0.85rem' }}>
          <div className="title">{c.comision}</div>
          {c.dia_horario && <div className="sub">{c.dia_horario}</div>}
          {c.funcion && (
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.83rem', whiteSpace: 'pre-wrap' }}>
              {c.funcion}
            </div>
          )}
          {c.miembros?.length > 0 && (
            <>
              <SubTitle><FaUsers /> Miembros ({c.miembros.length}) <span style={{ color: 'var(--text-disabled)', fontSize: '0.6rem', textTransform: 'none', letterSpacing: 'normal' }}>· click para abrir perfil</span></SubTitle>
              <MembersGrid>
                {c.miembros.map((m, i) => {
                  const normalized = flipLastFirstFront(m.nombre);
                  const slug = slugifyName(normalized);
                  return (
                    <MemberCard key={i} to={`/monitoring/hcd/autoridad/${slug}`}>
                      <span className="mini-avatar">{memberInitials(m.nombre)}</span>
                      <span className="mini-body">
                        <span className="mini-role">{m.cargo}</span>
                        <span className="mini-name">{normalized}</span>
                      </span>
                      <FaChevronRight className="mini-chev" />
                    </MemberCard>
                  );
                })}
              </MembersGrid>
            </>
          )}
        </Card>
      ))}
    </TechSection>
  );
}

function Mesa({ data, query }) {
  const [sub, setSub] = useState('expedientes');
  const subTabs = [
    { id: 'expedientes', label: `Expedientes (${data?.expedientes?.totals?.items_fetched ?? 0})` },
    { id: 'notas', label: `Notas (${data?.notas?.total ?? 0})` },
    { id: 'origenes', label: `Orígenes (${data?.origenes?.length ?? 0})` },
    { id: 'temas', label: `Temas (${data?.temas?.length ?? 0})` },
    { id: 'aprobaciones', label: `Aprobaciones (${data?.aprobaciones?.length ?? 0})` },
  ];

  return (
    <TechSection title="Mesa de Entrada">
      <SubTabsRow>
        {subTabs.map((s) => (
          <SubTab key={s.id} $active={sub === s.id} onClick={() => setSub(s.id)}>{s.label}</SubTab>
        ))}
      </SubTabsRow>

      {sub === 'expedientes' && (() => {
        const years = data?.expedientes?.fetched_years || [];
        const summary = data?.expedientes?.by_year_summary || [];
        const filteredYears = query
          ? years.map((y) => ({ ...y, items: y.items.filter((e) => JSON.stringify(e).toLowerCase().includes(query.toLowerCase())) }))
          : years;
        return (
          <>
            <Meta style={{ marginBottom: 8 }}>Total reportado por el sitio: <span style={{ color: accent }}>{data?.expedientes?.totals?.items_total_reported ?? 0}</span> expedientes en {summary.length} años. Detalle descargado para los últimos {years.length} años.</Meta>
            <SubTitle><FaCalendarAlt /> Resumen anual completo</SubTitle>
            <YearStrip>
              {summary.map((y) => (<TechChip key={y.anio}>{y.anio}: {y.cantidad}</TechChip>))}
            </YearStrip>
            {filteredYears.map((y) => (
              <Card key={y.anio} style={{ marginBottom: 12 }}>
                <div className="title">{y.anio} · {y.items.length} expedientes</div>
                {y.items.map((e, i) => (
                  <Row key={`${y.anio}-${i}`} href={e.detalle_url} target="_blank" rel="noreferrer">
                    <FaScroll className="icon" />
                    <div className="body">
                      <div><span className="num">Nro {e.nexpediente}</span> · {e.fecha}</div>
                      {e.tipo_proyecto && <div className="meta">{e.tipo_proyecto} · {e.texto_resolucion || '—'}</div>}
                      {e.texto_origen && <div className="meta">Origen: {e.texto_origen}</div>}
                      {e.texto_tema && <div className="meta">Tema: {e.texto_tema}</div>}
                      {e.caratula && <div style={{ marginTop: 4 }}>{e.caratula}</div>}
                    </div>
                  </Row>
                ))}
              </Card>
            ))}
          </>
        );
      })()}

      {sub === 'notas' && (() => {
        const items = data?.notas?.items || [];
        const filtered = query
          ? items.filter((n) => JSON.stringify(n).toLowerCase().includes(query.toLowerCase()))
          : items;
        const byYear = data?.notas?.by_year || [];
        return (
          <>
            <YearStrip>
              {byYear.map((y) => (<TechChip key={y.anio}>{y.anio}: {y.cantidad}</TechChip>))}
            </YearStrip>
            <ListBlock>
              {filtered.slice(0, 500).map((n, i) => (
                <Row key={i}>
                  <FaScroll className="icon" />
                  <div className="body">
                    <div><span className="num">Nota {n.numero}</span> · {n.fecha} · Acta {n.nroacta}</div>
                    <div className="meta">Origen: {n.origen} · Año: {n.anio}</div>
                    {n.descripcion && <div style={{ marginTop: 4 }}>{n.descripcion}</div>}
                  </div>
                </Row>
              ))}
            </ListBlock>
            {filtered.length > 500 && (
              <Meta style={{ marginTop: 10 }}>Mostrando 500 de {filtered.length}. Refiná con el buscador.</Meta>
            )}
          </>
        );
      })()}

      {sub === 'origenes' && (
        <ListBlock>
          {(data?.origenes || [])
            .filter((o) => !query || (o.origen || '').toLowerCase().includes(query.toLowerCase()))
            .map((o) => (
              <Row key={o.id}>
                <FaInfoCircle className="icon" />
                <div className="body"><div>{o.origen}</div><div className="meta">ID: {o.id}</div></div>
              </Row>
            ))}
        </ListBlock>
      )}

      {sub === 'temas' && (
        <ListBlock>
          {(data?.temas || [])
            .filter((t) => !query || `${t.tema} ${t.codigo}`.toLowerCase().includes(query.toLowerCase()))
            .slice(0, 500)
            .map((t) => (
              <Row key={t.id}>
                <FaInfoCircle className="icon" />
                <div className="body"><div>{t.tema}</div><div className="meta">Código: {t.codigo}</div></div>
              </Row>
            ))}
        </ListBlock>
      )}

      {sub === 'aprobaciones' && (
        <ListBlock>
          {(data?.aprobaciones || [])
            .filter((a) => !query || (a.aprobacion || '').toLowerCase().includes(query.toLowerCase()))
            .map((a) => (
              <Row key={a.id}>
                <FaInfoCircle className="icon" />
                <div className="body"><div>{a.aprobacion}</div><div className="meta">ID: {a.id}</div></div>
              </Row>
            ))}
        </ListBlock>
      )}
    </TechSection>
  );
}

function Sessions({ data, query }) {
  const byYear = data?.by_year || [];
  const [selectedYear, setSelectedYear] = useState(byYear[0]?.anio || null);
  useEffect(() => {
    if (!selectedYear && byYear.length) setSelectedYear(byYear[0].anio);
  }, [byYear, selectedYear]);

  const current = byYear.find((y) => y.anio === selectedYear) || byYear[0];
  const items = current?.actas || [];
  const filtered = query
    ? items.filter((a) => JSON.stringify(a).toLowerCase().includes(query.toLowerCase()))
    : items;

  return (
    <TechSection title={`Sesiones / Actas — ${data?.totals?.actas ?? 0} actas en ${data?.totals?.years ?? 0} años`}>
      <YearStrip>
        {byYear.map((y) => (
          <YearChip key={y.anio} $active={y.anio === selectedYear} onClick={() => setSelectedYear(y.anio)}>
            {y.anio} ({y.actas.length})
          </YearChip>
        ))}
      </YearStrip>
      {current ? (
        <CardGrid>
          {filtered.map((a) => (
            <Row key={a.id} href={a.pdf_url} target="_blank" rel="noreferrer">
              <FaFilePdf className="icon" style={{ color: 'var(--alert-error)' }} />
              <div className="body">
                <div><span className="num">Acta {a.numero}</span></div>
                <div className="meta">Fecha: {a.fecha}</div>
              </div>
              <FaExternalLinkAlt style={{ fontSize: '0.7rem' }} />
            </Row>
          ))}
        </CardGrid>
      ) : <Empty>Sin actas</Empty>}
    </TechSection>
  );
}

function Digesto({ data, query }) {
  const tipos = ['Ordenanzas', 'Decretos', 'Resoluciones', 'Comunicaciones'];
  const [sub, setSub] = useState('Ordenanzas');
  const section = data?.[sub];
  const items = section?.items || [];
  const filtered = query
    ? items.filter((i) => JSON.stringify(i).toLowerCase().includes(query.toLowerCase()))
    : items;
  const byYear = section?.by_year || [];

  return (
    <TechSection title="Digesto · Legislación">
      <SubTabsRow>
        {tipos.map((t) => (
          <SubTab key={t} $active={sub === t} onClick={() => setSub(t)}>
            {t} ({data?.[t]?.items?.length ?? 0})
          </SubTab>
        ))}
        <SubTab $active={sub === 'frecuentes'} onClick={() => setSub('frecuentes')}>
          Frecuentes ({data?.leyes_frecuentes?.length ?? 0})
        </SubTab>
      </SubTabsRow>

      {sub === 'frecuentes' ? (
        <ListBlock>
          {(data?.leyes_frecuentes || [])
            .filter((l) => !query || (l.nombre || '').toLowerCase().includes(query.toLowerCase()))
            .map((l) => (
              <Row key={l.id} href={l.url} target="_blank" rel="noreferrer">
                <FaBookOpen className="icon" />
                <div className="body"><div>{l.nombre}</div></div>
                <FaExternalLinkAlt style={{ fontSize: '0.7rem' }} />
              </Row>
            ))}
        </ListBlock>
      ) : (
        <>
          <YearStrip>
            {byYear.map((y, i) => (<TechChip key={i}>{y.anio}: {y.cantidad}</TechChip>))}
          </YearStrip>
          <ListBlock>
            {filtered.map((d) => (
              <Row key={d.id} href={d.pdf_url} target="_blank" rel="noreferrer">
                <FaFilePdf className="icon" style={{ color: 'var(--alert-error)' }} />
                <div className="body">
                  <div><span className="num">{d.tipo} Nº {d.numero}</span> · {d.fecha}</div>
                  {d.expediente && <div className="meta">Expediente: {d.expediente}</div>}
                  {d.resumen && <div style={{ marginTop: 4 }}>{d.resumen}</div>}
                </div>
                <FaExternalLinkAlt style={{ fontSize: '0.7rem' }} />
              </Row>
            ))}
          </ListBlock>
        </>
      )}
    </TechSection>
  );
}

function Press({ data, query }) {
  const allItems = [data?.ultima, ...(data?.anteriores || [])].filter(Boolean);
  const filtered = query
    ? allItems.filter((n) => JSON.stringify(n).toLowerCase().includes(query.toLowerCase()))
    : allItems;

  return (
    <TechSection title={`Prensa — ${data?.total ?? 0} noticias`}>
      {filtered.length === 0 ? <Empty>Sin noticias</Empty> : filtered.map((n) => (
        <Card key={n.id} style={{ marginBottom: '0.85rem' }}>
          <div className="title">{n.titulo}</div>
          {n.fecha && <div className="sub">{n.fecha}</div>}
          {n.copete && <div style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>{n.copete}</div>}
          {n.cuerpo_texto && (
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.83rem', whiteSpace: 'pre-wrap', marginTop: 6 }}>
              {n.cuerpo_texto.slice(0, 800)}{n.cuerpo_texto.length > 800 ? '…' : ''}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {n.detalle_url && (
              <a href={n.detalle_url} target="_blank" rel="noreferrer" style={{ color: accent, fontSize: '0.82rem' }}>
                Ver completa <FaExternalLinkAlt style={{ fontSize: '0.7rem' }} />
              </a>
            )}
            {n.imagen_url && (
              <a href={n.imagen_url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-disabled)', fontSize: '0.82rem' }}>
                Imagen
              </a>
            )}
          </div>
        </Card>
      ))}
    </TechSection>
  );
}

function History({ data }) {
  return (
    <TechSection title={`Historia — ${(data?.length ?? 0).toLocaleString('es-AR')} caracteres`}>
      {!data?.text ? <Empty>Sin contenido</Empty> : (
        <TextBlock>{data.text}</TextBlock>
      )}
    </TechSection>
  );
}

function CrawlView({ data, query }) {
  const pages = data?.pages || [];
  const filtered = query
    ? pages.filter((p) => `${p.url} ${p.title || ''} ${p.text || ''}`.toLowerCase().includes(query.toLowerCase()))
    : pages;
  return (
    <TechSection title={`Crawl genérico (${pages.length} páginas, ${data?.stats?.assets ?? 0} archivos)`}>
      <ListBlock>
        {filtered.slice(0, 100).map((p) => (
          <Row key={p.url} href={p.url} target="_blank" rel="noreferrer">
            <FaLink className="icon" />
            <div className="body">
              <div>{p.title || p.url}</div>
              <div className="meta">{p.url} · HTTP {p.status} · {p.text_length} chars</div>
            </div>
          </Row>
        ))}
      </ListBlock>
      <SubTitle><FaFilePdf /> Archivos descargables</SubTitle>
      <ListBlock>
        {(data?.assets || []).slice(0, 80).map((a) => (
          <Row key={a.url} href={a.url} target="_blank" rel="noreferrer">
            <FaFilePdf className="icon" style={{ color: 'var(--alert-error)' }} />
            <div className="body">
              <div>{a.text || a.url.split('/').pop()}</div>
              <div className="meta">{a.type}</div>
            </div>
          </Row>
        ))}
      </ListBlock>
    </TechSection>
  );
}

/* ---------- Main ---------- */

const HCDMonitoring = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [query, setQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const res = await getHcdSnapshot({ force });
      setData(res);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error al obtener datos');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const snapshot = data?.snapshot;
  const structured = snapshot?.structured || {};
  const crawl = snapshot?.crawl;
  const stats = structured?.stats;

  if (loading && !data) {
    return (
      <TechShell>
        <TechPageHeader title="Monitoreo · HCD San Nicolás" subtitle="Seguimiento completo del sitio del Honorable Concejo Deliberante" dossier="HCD-SCRAPE" />
        <Loading>Recorriendo el sitio del Concejo y trayendo la información… esto puede tardar 1-2 minutos</Loading>
      </TechShell>
    );
  }

  if (error) {
    return (
      <TechShell>
        <TechPageHeader title="Monitoreo · HCD San Nicolás" subtitle="Seguimiento completo del sitio del Honorable Concejo Deliberante" dossier="HCD-SCRAPE" />
        <ErrorBox>
          <FaExclamationTriangle style={{ marginRight: 8 }} /> {error}
          <div style={{ marginTop: 12 }}>
            <TechButton onClick={() => load(true)}>Reintentar</TechButton>
          </div>
        </ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Monitoreo · HCD San Nicolás"
        subtitle="Seguimiento completo del sitio del Honorable Concejo Deliberante"
        dossier="HCD-SCRAPE"
        actions={
          <TechButton onClick={() => load(true)} disabled={refreshing}>
            <FaSyncAlt style={{ marginRight: 6 }} />
            {refreshing ? 'Re-escaneando…' : 'Re-escanear'}
          </TechButton>
        }
      >
        <CronCountdown cronKey="hcd" label="Próxima actualización en" />
      </TechPageHeader>

      <HcdChat />

      <TechTabs tabs={TABS} active={tab} onChange={setTab} />

      <Toolbar>
        <SearchWrap>
          <FaSearch />
          <TechInput
            placeholder="Buscar dentro de la sección actual…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchWrap>
        <Meta><FaClock /><span>Snapshot: {formatDate(data?.cached_at)}</span></Meta>
        <Meta><FaInfoCircle /><span>{data?.source === 'cache' ? `Caché · ${data?.age_seconds}s` : 'Fresco'}</span></Meta>
      </Toolbar>

      {tab === 'overview' && <Overview snapshot={snapshot} stats={stats} />}
      {tab === 'search' && <RagSearch />}
      {tab === 'authorities' && <Authorities items={structured.autoridades} query={query} />}
      {tab === 'committees' && <Committees items={structured.comisiones} query={query} />}
      {tab === 'mesa' && <Mesa data={structured.mesa} query={query} />}
      {tab === 'sessions' && <Sessions data={structured.actas} query={query} />}
      {tab === 'digesto' && <Digesto data={structured.digesto} query={query} />}
      {tab === 'press' && <Press data={structured.prensa} query={query} />}
      {tab === 'history' && <History data={structured.historia} />}
      {tab === 'crawl' && <CrawlView data={crawl} query={query} />}
    </TechShell>
  );
};

export default HCDMonitoring;
